import { IAnalysisUseCase } from './i-analysis-use-case'
import { IQueryRepository } from '../repositories/i-query-repository'
import { LayerEntity } from '../models/entities/layer-entity'
import { AnalysisResultEntity } from '../models/entities/analysis-result-entity'
import { ResponseValueObject } from '../models/value-objects/response-value-object'
import { StatisticDefinition } from '../models/entities/statistic-definition-entity'
import { StatisticTypeEnum } from '../models/enums/statistic-type-enum'
import { LayerStatisticEntity } from '../models/entities/layer-statistic-entity'

export class OverlappingRightsUseCase implements IAnalysisUseCase {
  readonly id = 'overlapping-rights'

  private static readonly GROUP_STAT: StatisticDefinition[] = [{
    statisticType: StatisticTypeEnum.Count,
    onStatisticField: '1',
    outStatisticFieldName: 'count',
  }]

  constructor(private readonly queryRepo: IQueryRepository) {}

  private buildWhere(entity: LayerEntity, code: string | null | undefined, name: string | null | undefined): string {
    if (!entity.definitionQuery || (!code && !name)) return '1=1'
    const e = new LayerEntity(entity)
    e.setValue(entity.definitionQueryWithLabel ? (name ?? null) : (code ?? null))
    return e.definitionQuery ?? '1=1'
  }

  async execute(layers: LayerEntity[], departmentCode?: string | null, departmentName?: string | null): Promise<ResponseValueObject<AnalysisResultEntity>> {
    const extentLayerIdx = layers.findIndex(c => !!(departmentCode && c.definitionQuery && departmentName))
    const validIdx = extentLayerIdx >= 0 ? extentLayerIdx : 0
    const extentLayer = layers[validIdx]
    const baseWhere = this.buildWhere(extentLayer, departmentCode, departmentName)
    const extentRes = await this.queryRepo.queryLayerExtent(extentLayer.url ?? '', baseWhere)
    const baseExtent = extentRes.data

    const results = await Promise.all(
      layers.map(async (c, i) => {
        const where = this.buildWhere(c, departmentCode, departmentName)

        const totalCount = (await this.queryRepo.queryLayerCount(c.url ?? '', where)).data ?? 0

        let categoryStats: LayerStatisticEntity[] | undefined
        if (c.categoryField) {
          const catRes = await this.queryRepo.queryLayerStatistics(c.url ?? '', where, OverlappingRightsUseCase.GROUP_STAT, [c.categoryField])
          categoryStats = catRes.data ?? undefined
        }

        let spatialCrossCount: number | undefined
        if (i !== validIdx && baseExtent) {
          spatialCrossCount = (await this.queryRepo.queryLayerCount(c.url ?? '', where, baseExtent)).data
        }

        return {
          layerName: c.name,
          layerUrl: c.url ?? '',
          featureCount: totalCount,
          categoryStats,
          spatialCrossCount: spatialCrossCount ?? totalCount,
        }
      })
    )

    return new ResponseValueObject(true, {
      analysisId: this.id,
      analysisName: 'Superposición de Derechos sobre el Territorio',
      completedAt: new Date(),
      layerResults: results,
      totalFeatures: results.reduce((s, r) => s + r.featureCount, 0),
    })
  }
}
